import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';
import {cssSchema} from '../../schemas';

const toolName = 'render-css' as const;

const argumentZod = z.object({
  css: cssSchema,
});

export const renderCssTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Render styles',
  prettyStatus: {
    executing: 'Rendering styles...',
    executed: 'Rendered styles',
    failed: 'Failed to render styles',
  },
  prettyErrors: {
    fallback: 'Unable to apply the styles. Please try again.',
    patterns: [
      {
        match: 'No CSS provided',
        message: 'No CSS was provided to render.',
      },
      {
        match: '@keyframes animations are not allowed',
        message: 'CSS animations are not supported. Please remove @keyframes.',
      },
      {
        match: '@import statements are not allowed',
        message: '@import is not supported. Include styles directly.',
      },
      {
        match: '!important declarations are not allowed',
        message: '!important is not supported. Please remove it from your CSS.',
      },
      {
        match: 'Nested selectors are not allowed',
        message:
          "Nested selectors are not supported. Use a single class like '.card-image' instead of '.card img'.",
      },
      {
        match: '::before and ::after pseudo-elements are not allowed',
        message:
          '::before and ::after pseudo-elements are not supported in Webflow.',
      },
      {
        match: 'Attribute selectors are not allowed',
        message:
          'CSS attribute selectors are not supported. Use class selectors instead.',
      },
      {
        match: 'Universal selector (*) is not allowed',
        message:
          'The universal selector (*) is not supported. Target specific elements.',
      },
      {
        match: 'ID selectors are not allowed',
        message:
          'CSS ID selectors are not supported. Use class selectors instead.',
      },
      {
        match: 'Element selectors are not allowed',
        message:
          "Element selectors like 'img' or 'div' are not supported. Add a class to the element and target that class.",
      },
      {
        match: 'Shorthand properties are not allowed',
        message:
          "Use longhand properties (e.g. 'margin-top' instead of 'margin', 'padding-left' instead of 'padding').",
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Applies CSS styles to existing elements in the Webflow Designer. Use instead of render when only styles need to change — no HTML modification. No CSS animations. Use render-variables to create or update CSS variables.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SiteStyleBlock', action: 'modify'}],
  confirmBeforeExecuting: true, // This is destructive, so require confirmation
  timeout: 30000, // 30 seconds for removing style property
});
